import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Html5Qrcode } from "html5-qrcode";
import _ from 'lodash';
import { useAppData } from './context/DataContext';
import { verifyTicket } from './tickets.service';

const Scanner = ({ event_id, style, onScanComplete, attendees, checkedInAttendees, fromQR = false, qrUserId = null, modalOpen }) => {
    // Use a ref to keep track of the scanner instance
    const html5QrCodeRef = useRef(null);
    // Use a ref to track if the scanner is running
    const isScannerRunningRef = useRef(false);

    useEffect(() => {
        if (fromQR && qrUserId) {
            const mockUrl = `${BASE_URL}/${event_id}?userId=${qrUserId}`;
            processQrCode(mockUrl);
        }
    }, [fromQR, qrUserId]);

    const { users } = useAppData();

    function getEmailFromId(userId) {
        if (!users || users.length === 0) return "";
        const userIndex = users.findIndex(user => user._id === userId);
        if (userIndex === -1) return "";
        return users[userIndex]?.email || "";
    }
    const [borderColor, setBorderColor] = useState('transparent');

    const BASE_URL = "https://www.ummahfy.com/event"

    const showBorderFlash = (success) => {
        setBorderColor(success ? '#4CAF50' : '#FF5252');
        setTimeout(() => {
            setBorderColor('transparent');
        }, 500);
    };

    const validateUrl = useCallback((url) => {
        try {
            const parsedUrl = new URL(url);
            const pathParts = parsedUrl.pathname.split('/');
            const scannedEventId = pathParts[pathParts.indexOf('event') + 1];

            if (!url.startsWith(`${BASE_URL}/`) || scannedEventId != event_id) {
                console.log("Event ID mismatch:", scannedEventId, event_id);
                return { valid: false };
            }

            // Only accept ticketId format
            const ticketId = parsedUrl.searchParams.get('ticketId');
            if (ticketId) {
                console.log("Valid ticket ID found:", ticketId);
                return { valid: true, id: ticketId };
            }
            
            console.log("No ticketId found in URL");
            return { valid: false };
        } catch (error) {
            console.error('Error parsing URL:', error);
            return { valid: false };
        }
    }, [event_id]);

    const validateUser = async (userId) => {
        // TODO: Implement user validation logic
        // get User's email from id
        let userEmail = ""
        try {
            userEmail = getEmailFromId(userId)
            console.log(userEmail)
        } catch {
            return false
        }
        // check if email is in registered/approvedAttendees
        return attendees.includes(userEmail); // true
    };

    // Memoize the debounced function so it doesn't recreate on every render
    const processQrCode = useCallback(
        _.debounce(async (decodedText) => {
            try {
                console.log(`QR Code detected: ${decodedText}`);
                let err = "";
                let result = null;

                const validationResult = validateUrl(decodedText);

                if (!validationResult.valid) {
                    console.log('Invalid QR code format');
                    err = "Invalid Ticket";
                    showBorderFlash(false);
                    onScanComplete(null, event_id, err);
                    return;
                }
                
                // Verify the ticket
                try {
                    console.log("Verifying ticket:", validationResult.id);
                    const response = await verifyTicket(validationResult.id);
                    
                    if (response.valid) {
                        console.log("Ticket verified successfully:", response);
                        showBorderFlash(true);
                        // Pass the ticket information to the callback
                        result = {
                            email: response.ticketData.email,
                            ticketId: validationResult.id,
                            ticketData: response.ticketData
                        };
                    } else {
                        console.log("Invalid ticket:", response);
                        err = "Invalid Ticket";
                        showBorderFlash(false);
                    }
                } catch (error) {
                    console.error('Error verifying ticket:', error);
                    err = "Verification Failed";
                    showBorderFlash(false);
                }

                console.log("Scan result:", result);

                // Call the callback with the result
                onScanComplete(
                    result,
                    event_id,
                    err
                );

            } catch (error) {
                console.error('Error processing QR code:', error);
                onScanComplete(null, event_id, "Processing Error");
            }
        }, 1000, { leading: true, trailing: false }),
        [validateUrl, verifyTicket]
    );

    useEffect(() => {
        const qrCodeScannerId = "qr-code-scanner";

        const startScanner = async () => {
            if (html5QrCodeRef.current) {
                return; // Scanner already exists
            }

            const qrCodeScannerContainer = document.getElementById(qrCodeScannerId);
            if (!qrCodeScannerContainer) {
                console.error("QR Code scanner container not found.");
                return;
            }

            html5QrCodeRef.current = new Html5Qrcode(qrCodeScannerId);

            const config = {
                fps: 15,
                qrbox: {
                    width: window.innerWidth,
                    height: window.innerHeight
                }
            };

            try {
                await html5QrCodeRef.current.start(
                    { facingMode: "environment" },
                    config,
                    async (decodedText, decodedResult) => {
                        await processQrCode(decodedText);
                    },
                    (errorMessage) => {
                        const ignoredErrors = [
                            'No barcode or QR code detected',
                            'No MultiFormat Readers were able to detect the code',
                            'NotFoundException'
                        ];

                        if (!ignoredErrors.some(error => errorMessage.includes(error))) {
                            console.warn(`QR Code scan error: ${errorMessage}`);
                        }
                    }
                );
                isScannerRunningRef.current = true;
            } catch (err) {
                console.error("Error initializing QR Code scanner: ", err);
            }
        };

        const stopCamera = async () => {
            if (html5QrCodeRef.current && isScannerRunningRef.current) {
                try {
                    await html5QrCodeRef.current.stop();
                    console.log("Camera stopped successfully.");
                } catch (err) {
                    console.error("Error stopping the camera:", err);
                } finally {
                    html5QrCodeRef.current = null;
                    isScannerRunningRef.current = false;
                }
            }
        };

        // Fixed camera logic using refs for proper state management
        if (modalOpen) {
            console.log("starting camera")
            startScanner();
        } else {
            console.log("stopping camera")
            stopCamera();
        }

        // Cleanup function
        return () => {
            if (html5QrCodeRef.current) {
                html5QrCodeRef.current.stop().catch(err => console.error("Error stopping scanner: ", err));
                html5QrCodeRef.current = null;
                isScannerRunningRef.current = false;
            }
        };
    }, [processQrCode, modalOpen]); // Only depend on processQrCode which is now memoized

    return (
        <div>
            <div id="qr-code-scanner" className="qr-container" style={{
                ...style,
                border: `3px solid ${borderColor}`,
                transition: 'border-color 0.2s ease'
            }}></div>
        </div>
    );
};

export default Scanner;